import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, TextField, Button, Select, MenuItem, Chip,
  IconButton, Dialog, DialogActions, DialogContent, Grid, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  useMediaQuery, useTheme, Card
} from '@mui/material';
import { Edit, Delete, DragIndicator } from '@mui/icons-material';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useTranslation } from 'react-i18next';
import api from '../api/axiosConfig';

const ItemTypes = { TAG: 'tag' };

// Codes couleur disponibles (les noms sont traduits dynamiquement)
const colorCodes = [
  '#ffcc80',
  '#90caf9',
  '#a5d6a7',
  '#f48fb1',
  '#ffab91',
  '#ce93d8',
  '#81c784',
  '#64b5f6',
  '#ba68c8',
  '#4fc3f7',
  '#e57373',
  '#fff176',
];

/* ---------- ligne draggable ---------- */
const TagRow = ({ tag, index, moveTag, onEdit, onDelete, isSmallScreen, t }) => {
  const ref = useRef(null);

  const [, drop] = useDrop({
    accept: ItemTypes.TAG,
    hover(item) {
      if (item.index === index) return;
      moveTag(item.index, index);
      item.index = index; // mise à jour de l'index de l'item en cours de drag
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.TAG,
    item: { id: tag.id, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(drop(ref));

  // Obtenir le nom traduit de la couleur
  const getColorName = (code) => t(`tags.colors.${code}`, code);

  return (
    <TableRow
      ref={ref}
      hover
      sx={{
        opacity: isDragging ? 0.6 : 1,
        cursor: 'move',
      }}
    >
      <TableCell sx={{ pl: 1, width: 40 }}>
        <DragIndicator fontSize="small" />
      </TableCell>
      <TableCell>
        <Chip
          label={tag.libelle}
          sx={{ backgroundColor: tag.code_couleur, color: '#fff' }}
        />
      </TableCell>
      <TableCell>
        <Card
          sx={{
            backgroundColor: tag.code_couleur,
            color: '#fff',
            padding: '4px 12px',
            display: 'inline-flex',
            alignItems: 'center',
            borderRadius: '8px',
            boxShadow: 1,
            fontWeight: 500,
            fontSize: '0.875rem'
          }}
        >
          {getColorName(tag.code_couleur)}
        </Card>
      </TableCell>
      <TableCell align="right">
        <IconButton onClick={() => onEdit(tag)} sx={{ color: '#ff9800' }}>
          <Edit />
        </IconButton>
        <IconButton onClick={() => onDelete(tag)} sx={{ color: 'red' }}>
          <Delete />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

/* ---------- composant principal ---------- */
const Tags = () => {
  const { t } = useTranslation();
  const [tagName, setTagName] = useState('');
  const [tagColor, setTagColor] = useState('');
  const [createBigboards, setCreateBigboards] = useState(false);
  const [tags, setTags] = useState([]);
  const [editTag, setEditTag] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const theme         = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // Helper pour obtenir le nom traduit d'une couleur
  const getColorName = (code) => t(`tags.colors.${code}`, code);

  /* ----------- helpers ----------- */

  /** Trie par ordre (NULL à la fin) puis libellé */
  const sortTags = useCallback((arr) => {
    return [...arr].sort((a, b) => {
      if (a.ordre === null && b.ordre === null)
        return a.libelle.localeCompare(b.libelle, 'es', { sensitivity: 'base' });
      if (a.ordre === null) return 1;
      if (b.ordre === null) return -1;
      return a.ordre - b.ordre;
    });
  }, []);

  const fetchTags = async () => {
    try {
      const { data } = await api.get('/api/tags');
      setTags(sortTags(data));
    } catch (err) {
      console.error(t('tags.fetchError'), err);
    }
  };

  /* ----------- chargement initial ----------- */
  useEffect(() => { fetchTags(); }, []);

  /* ----------- création ----------- */
  const handleAddTag = async () => {
    if (!tagName || !tagColor) return;
    try {
      const user     = JSON.parse(localStorage.getItem('user'));
      const { data } = await api.post('/api/tags', {
        libelle: tagName,
        code_couleur: tagColor,
        create_bigboards: createBigboards,
        user_id: user.id
      });
      setTags(sortTags([...tags, data]));
      setTagName('');
      setTagColor('');
      setCreateBigboards(false);
    } catch (err) {
      console.error(t('tags.addError'), err);
    }
  };

  /* ----------- édition ----------- */
  const handleEditTag = (tag) => {
    setEditTag(tag);
    setTagName(tag.libelle);
    setTagColor(tag.code_couleur);
  };

  const handleSaveEdit = async () => {
    try {
      await api.put(`/api/tags/${editTag.id}`, {
        libelle: tagName,
        code_couleur: tagColor
      });
      const updated = tags.map(tg =>
        tg.id === editTag.id ? { ...tg, libelle: tagName, code_couleur: tagColor } : tg
      );
      setTags(sortTags(updated));
      setTagName('');
      setTagColor('');
      setEditTag(null);
    } catch (err) {
      console.error(t('tags.updateError'), err);
    }
  };

  /* ----------- suppression ----------- */
  const handleDeleteTag = async (tag) => {
    try {
      await api.delete(`/api/tags/${tag.id}`);
      setTags(sortTags(tags.filter(tg => tg.id !== tag.id)));
      setConfirmDelete(null);
    } catch (err) {
      console.error(t('tags.deleteError'), err);
    }
  };

  /* ----------- drag & drop ----------- */
  const moveTag = (fromIndex, toIndex) => {
    setTags(prev => {
      const updated = [...prev];
      const [removed] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, removed);

      // ré‑indexation locale immédiate
      return updated.map((t, idx) => ({ ...t, ordre: idx + 1 }));
    });
  };

  // persistance en base à chaque changement d'ordre
  useEffect(() => {
    if (tags.length === 0) return;
    const persist = async () => {
      try {
        await api.put('/api/tags/reorder', {
          orders: tags.map(({ id, ordre }) => ({ id, ordre })),
        });
      } catch (err) {
        console.error(t('tags.reorderError'), err);
        fetchTags(); // rollback si erreur
      }
    };
    persist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tags]);

  /* ----------- rendu ----------- */
  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h4" sx={{ mb: 4 }}>{t('tags.title')}</Typography>

      {/* ------ formulaire de création / édition ------ */}
      <Grid container spacing={2} direction={isSmallScreen ? 'column' : 'row'} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <TextField
            label={t('tags.tagName')}
            variant="outlined"
            value={tagName}
            onChange={(e) => setTagName(e.target.value)}
            fullWidth
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <Select
            value={tagColor}
            onChange={(e) => setTagColor(e.target.value)}
            displayEmpty
            variant="outlined"
            fullWidth
            renderValue={(selected) => {
              if (!selected) {
                return <em>{t('tags.selectColor')}</em>;
              }
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      backgroundColor: selected,
                      borderRadius: '4px',
                      border: '1px solid rgba(0,0,0,0.1)'
                    }}
                  />
                  {getColorName(selected)}
                </Box>
              );
            }}
          >
            <MenuItem value="" disabled>{t('tags.selectColor')}</MenuItem>
            {colorCodes.map((code) => (
              <MenuItem key={code} value={code}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      backgroundColor: code,
                      borderRadius: '4px',
                      border: '1px solid rgba(0,0,0,0.1)'
                    }}
                  />
                  {getColorName(code)}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Button
            variant="contained"
            onClick={editTag ? handleSaveEdit : handleAddTag}
            sx={{ backgroundColor: '#ff9800', color: '#fff', height: '56px', width: '180px' }}
          >
            {editTag ? t('tags.saveChanges') : t('tags.addTag')}
          </Button>
        </Grid>
      </Grid>

      {/* ------ tableau draggable ------ */}
      <Typography variant="h6" sx={{ mb: 2 }}>{t('tags.orderTags')}</Typography>

      <DndProvider backend={HTML5Backend}>
        <TableContainer component={Paper}>
          <Table size={isSmallScreen ? 'small' : 'medium'}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40 }} />
                <TableCell>{t('tags.label')}</TableCell>
                <TableCell>{t('tags.color')}</TableCell>
                <TableCell align="right">{t('tags.actions')}</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {tags.map((tag, index) => (
                <TagRow
                  key={tag.id}
                  tag={tag}
                  index={index}
                  moveTag={moveTag}
                  onEdit={handleEditTag}
                  onDelete={setConfirmDelete}
                  isSmallScreen={isSmallScreen}
                  t={t}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DndProvider>

      {/* ------ boîte de confirmation suppression ------ */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogContent>
          <Typography>{t('tags.confirmDelete', { name: confirmDelete?.libelle })}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} sx={{ color: 'grey' }}>{t('tags.cancel')}</Button>
          <Button onClick={() => handleDeleteTag(confirmDelete)} sx={{ color: 'red' }}>{t('tags.delete')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Tags;